import { memo, useEffect, useMemo } from 'react';

import { isAccessEnded } from '../../helpers/utils/functions';
import MainLayout from '../../shared/common/components/layouts/MainLayout';
import Loader from '../../shared/common/components/loader/Loader';
import CreateMetricsModal from '../../shared/common/components/reports/CreateMetricsModal';
import { listKeysAdminGroupings, listKeysGroupingsWebinar as listKeysGroupings, listKeysMetricsWebinar as listKeysMetrics } from '../../shared/common/components/reports/modal-settings/index';
import FirstRegIcon from '../../shared/common/components/reports/params/attribution/icons/FirstRegIcon';
import LastRegIcon from '../../shared/common/components/reports/params/attribution/icons/LastRegIcon';
import TypeIcon from '../../shared/common/components/reports/params/attribution/icons/TypeIcon';
import Params from '../../shared/common/components/reports/params/Params';
import UniversalReportTitle from '../../shared/common/components/reports/un-title/UniversalReportTitle';
import ReportChart from '../../shared/common/components/reports/chart/ReportChart';
import ReportTable from '../../shared/common/components/reports/report-table';
import { chartDetails } from '../../shared/common/constants/details';
import { createAttrItems } from '../../shared/common/constants/report-constants';
import usePermission from '../../shared/common/hooks/usePermission';
import { useTypedSelector } from '../../shared/common/hooks/useTypedSelector';
import { useReportPageInit } from '../../shared/common/hooks/useReportPageInit';

interface ListKeyItem {
    dataKey: string;
    name?: string;
    label?: string;
    [key: string]: unknown;
}

export const defaultGroupingsWebinar = [ // Дефолтные группировки
    'reg_utm_source_4',
    'reg_utm_medium_4',
    'reg_utm_campaign_4',
    'web_duration_4',
    'web_name_4'
].map((dataKey) => listKeysGroupings.find((item: ListKeyItem) => item.dataKey === dataKey));

export const defaultMetricsWebinar = [ // Дефолтные метрики
    'web_users_count_4',
    'web_15min_users_count_4',
    'web_60min_g_users_count_4',
    'web_15min_users_part_4',
    'web_60min_g_users_part_4',
    'web_clickers_count_4',
    'clicker_to_creator_converse_4',
    'web_user_to_creator_converse_4',
    'orders_count_4',
    'orders_creators_count_4',
    'orders_sum_4',
    'orders_sum_avg_web_4',
    'payments_count_4',
    'buyers_count_4',
    'payments_sum_4',
    'web_to_buyer_converse_4',
    'payments_sum_per_web_4',
    'creator_to_buyer_converse_4'
].map((dataKey) =>
    listKeysMetrics.find((item: ListKeyItem) => item.dataKey === dataKey));

const defaultMetricsWebNotPrepareNumber = [
    'web_users_count_4',
].map((dataKey) =>
    listKeysMetrics.find((item: ListKeyItem) => item.dataKey === dataKey)
);

export const defaultMetricsWeb = defaultMetricsWebNotPrepareNumber
    .filter((item) => item)
    .map((item) => ({
        ...item,
        dataKey: item.dataKey,
    }));


const defaultGroupingsWebNotPrepareNumber = [
    'web_date_y_4',
].map((dataKey) =>
    listKeysGroupings.find((item: ListKeyItem) => item.dataKey === dataKey)
);

export const defaultGroupingWeb = defaultGroupingsWebNotPrepareNumber
    .filter((item) => item)
    .map((item) => ({
        ...item,
        dataKey: item.dataKey,
    }));

const WebinarReports = () => {
    const checkPermission = usePermission();
    
    // Определяем дефолтные группировки в зависимости от прав пользователя
    const defaultGroupings = useMemo(() => [
        'reg_utm_source_4',
        'reg_utm_medium_4',
        'reg_utm_campaign_4',
        'web_duration_4',
        'web_date_y_4'
    ].map((dataKey) => checkPermission(['superadmin']) 
        ? listKeysAdminGroupings.find((item: ListKeyItem) => item.dataKey === dataKey) 
        : listKeysGroupings.find((item: ListKeyItem) => item.dataKey === dataKey))
        .filter((item): item is ListKeyItem => item !== undefined), [checkPermission]);
    
    const defaultMetrics = useMemo(() => [
        'web_users_count_4',
        'web_15min_users_count_4',
        'web_60min_g_users_count_4',
        'web_15min_users_part_4',
        'web_60min_g_users_part_4',
        'web_clickers_count_4',
        'clicker_to_creator_converse_4',
        'web_user_to_creator_converse_4',
        'orders_count_4',
        'orders_creators_count_4',
        'orders_sum_4',
        'orders_sum_avg_web_4',
        'payments_count_4',
        'buyers_count_4',
        'payments_sum_4',
        'web_to_buyer_converse_4',
        'payments_sum_per_web_4',
        'creator_to_buyer_converse_4'
    ].map((dataKey) =>
        listKeysMetrics.find((item: ListKeyItem) => item.dataKey === dataKey))
        .filter((item): item is ListKeyItem => item !== undefined), []);
    
    // Используем универсальный хук для инициализации страницы отчёта
    const { isChartLoading } = useReportPageInit({
        defaultMetrics,
        defaultGroupings,
    });
    
    useEffect(() => {
        document.body.style.overflow = 'scroll';
        document.body.style.overflowX = 'auto';
        
        return () => {
            document.body.style.overflow = '';
            document.body.style.overflowX = '';
        };
    }, []);
    
    const attrItems = createAttrItems(<TypeIcon />, <FirstRegIcon />, <LastRegIcon />);
    const { isCreateMetricsVisible } = useTypedSelector((state) => state.reports);
    const accessEnded = isAccessEnded();
    
    return (
        <>
            <MainLayout pageTitle="Отчёт по вебинарам">
                <div className="page-wrapper">
                    {accessEnded ? (
                        <iframe
                            style={{ border: 'none', width: '990px', float: 'left', minHeight: '100vh', marginTop: '2vh' }}
                            src={`https://doc.scananalytics.ru/oshibki-i-preduprezhdeniya/dostup-k-otchetu-po-vebinaram-zakryt/?timestamp=${new Date().getTime()}`}
                            width="100%"
                            height="100%"
                        />
                    ) : (
                        <div className="page-content">
                            <UniversalReportTitle />
                            <Params
                                attributionItems={attrItems}
                                detailsItems={chartDetails}
                                filtersFilter={[
                                    'Пользователи',
                                    'Регистрации',
                                    'Заказы',
                                    'Платежи',
                                    'Вебинары',
                                ]}
                            />
                            <Loader loading={isChartLoading}>
                                <ReportChart reportType="webinar" />
                            </Loader>
                            <ReportTable reportType="webinar" />
                        </div>
                    )}
                </div>
            </MainLayout>

            {isCreateMetricsVisible && (<CreateMetricsModal title="Создание метрики в отчете по вебинарам" />)}
        </>
    );
};

export default memo(WebinarReports);
